import React, { useEffect, useState } from 'react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from '@chakra-ui/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDownIcon } from '@chakra-ui/icons';

const removeTrailingSlash = (str) => str.replace(/\/$/, '');

export const BreadcrumbNav = ({ rootPath: rawRootPath, pathTree }) => {
  const rootPath = rawRootPath && removeTrailingSlash(rawRootPath);
  const location = useLocation();
  const navigate = useNavigate();
  const [currentPathSegments, setCurrentPathSegments] = useState([]);

  useEffect(() => {
    const updateCurrentPathSegments = (newLocation) => {
      const pathWithoutRoot = newLocation.pathname.replace(new RegExp(`^${rootPath}/`), '');
      const segments = pathWithoutRoot.split('/').filter(Boolean);
      setCurrentPathSegments(segments);
    };

    updateCurrentPathSegments(location);
  }, [location, rootPath]);

  const buildFullPath = (segments) => `${rootPath}/${segments.join('/')}`;

  const findNodeAndParent = (segment) => {
    const queue = [{ node: null, parent: null, children: pathTree }];

    while (queue.length > 0) {
      const { node, children } = queue.shift();

      for (const child of children) {
        if (child.segment === segment) return { node: child, parent: node };
        if (child.children) queue.push({ node: child, parent: node, children: child.children });
      }
    }

    return { node: null, parent: null };
  };

  const renderSegment = ({ segment, segmentsToParent, isLast }) => {
    const { node, parent } = findNodeAndParent(segment);
    if (!node) return null;

    const path = buildFullPath([...segmentsToParent, node.segment]);
    const siblings = (parent?.children ?? []).filter((child) => child.segment !== segment);

    const handleSiblingClick = (sibling) => {
      const siblingPath = buildFullPath([...segmentsToParent, sibling.segment]);
      navigate(siblingPath);
    };

    return (
      <BreadcrumbItem key={node.segment} isCurrentPage={isLast}>
        {siblings.length ? (
          <Menu>
            <MenuButton as={Button} rightIcon={<ChevronDownIcon />} variant="link">
              {node.name}
            </MenuButton>
            <MenuList>
              {siblings.map((sibling) => (
                <MenuItem key={sibling.segment} onClick={() => handleSiblingClick(sibling)}>
                  {sibling.name}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        ) : (
          <BreadcrumbLink as={Link} to={!isLast && path}>
            {node.name}
          </BreadcrumbLink>
        )}
      </BreadcrumbItem>
    );
  };

  return (
    <Breadcrumb>
      {currentPathSegments.map((segment, index) => {
        const isLast = index === currentPathSegments.length - 1;
        const segmentsToParent = currentPathSegments.slice(0, index);
        return renderSegment({ segment, isLast, segmentsToParent });
      })}
    </Breadcrumb>
  );
};
